// Ported from the reference games' ui/EmailAuthDialog.jsx: same login/sign-up
// flow, verification messaging, error-code mapping and Escape handling. Class
// names are kept identical so the styling is the shared one.

import { useEffect, useState, type FormEvent } from "react";
import { t } from "../i18n.ts";
import type { GameUser } from "../auth/AuthContext.tsx";
import "./EmailAuthDialog.css";

interface CodedError {
  code?: string;
  message?: string;
}

function authErrorMessage(error: CodedError | null): string {
  switch (error?.code) {
    case "auth/email-not-verified":
      return t("emailAuthNotVerified");
    case "auth/email-already-in-use":
      return t("emailAuthAlreadyUsed");
    case "auth/invalid-email":
      return t("emailAuthInvalidEmail");
    case "auth/weak-password":
      return t("emailAuthWeakPassword");
    case "auth/invalid-credential":
    case "auth/user-not-found":
    case "auth/wrong-password":
      return t("emailAuthInvalidCredential");
    case "auth/too-many-requests":
      return t("emailAuthTooManyRequests");
    case "auth/google-native-app-required":
      return t("googleAuthAndroidOnly");
    case "auth/google-email-not-verified":
      return t("googleAuthNotVerified");
    default:
      return error?.message || t("loginFailed");
  }
}

interface Props {
  open: boolean;
  onClose: () => void;
  configured: boolean;
  signInWithEmail: (email: string, password: string) => Promise<GameUser>;
  signInWithGoogle: () => Promise<GameUser>;
  signUpWithEmail: (email: string, password: string, displayName?: string) => Promise<void>;
  resendVerification: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

export default function EmailAuthDialog({
  open,
  onClose,
  configured,
  signInWithEmail,
  signInWithGoogle,
  signUpWithEmail,
  resendVerification,
  resetPassword,
}: Props) {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMessage("");
    setError("");
    setCanResend(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      event.stopImmediatePropagation();
      if (event.key === "Escape" && !busy) {
        event.preventDefault();
        onClose();
      }
    };
    const onKeyUp = (event: KeyboardEvent) => event.stopImmediatePropagation();
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("keyup", onKeyUp, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp, true);
    };
  }, [open, busy, onClose]);

  if (!open) return null;

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setMessage("");
    setError("");
    try {
      await action();
      return true;
    } catch (err) {
      const e = err as CodedError;
      setCanResend(e?.code === "auth/email-not-verified");
      setError(authErrorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!configured) return setError(t("firebaseUnconfigured"));
    if (!email.trim()) return setError(t("emailAuthEnterEmail"));
    if (!password) return setError(t("emailAuthEnterPassword"));
    if (password.length < 6) return setError(t("emailAuthWeakPassword"));
    if (mode === "signup") {
      if (password !== passwordConfirm) return setError(t("emailAuthPasswordMismatch"));
      const ok = await run(() => signUpWithEmail(email, password, displayName));
      if (ok) {
        setMode("login");
        setPassword("");
        setPasswordConfirm("");
        setMessage(t("emailAuthVerificationSent"));
      }
      return;
    }
    const ok = await run(() => signInWithEmail(email, password));
    if (ok) onClose();
  };

  const resend = async () => {
    const ok = await run(() => resendVerification(email, password));
    if (ok) setMessage(t("emailAuthVerificationResent"));
  };

  const reset = async () => {
    if (!email.trim()) return setError(t("emailAuthEnterEmail"));
    const ok = await run(() => resetPassword(email));
    if (ok) setMessage(t("emailAuthResetSent"));
  };

  const signInGoogle = async () => {
    const ok = await run(signInWithGoogle);
    if (ok) onClose();
  };

  return (
    <div
      className="email-auth-backdrop"
      role="presentation"
      onMouseDown={() => !busy && onClose()}
    >
      <section
        className="email-auth-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-auth-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          className="email-auth-close"
          type="button"
          aria-label={t("close")}
          onClick={onClose}
          disabled={busy}
        >
          ×
        </button>

        <h2 id="email-auth-title">
          {mode === "signup" ? t("emailAuthCreateTitle") : t("emailAuthLoginTitle")}
        </h2>
        <p className="email-auth-subtitle">{t("emailAuthSubtitle")}</p>

        <form className="email-auth-form" onSubmit={submit} noValidate>
          {mode === "signup" && (
            <label>
              <span>{t("emailAuthNickname")}</span>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={24}
                autoComplete="nickname"
              />
            </label>
          )}

          <label>
            <span>{t("emailAuthEmail")}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </label>

          <label>
            <span>
              {t("emailAuthPassword")}
              {mode === "signup" && (
                <small className="email-auth-label-hint">{t("emailAuthPasswordHint")}</small>
              )}
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              minLength={6}
              required
            />
          </label>

          {mode === "signup" && (
            <label>
              <span>{t("emailAuthPasswordConfirm")}</span>
              <input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                autoComplete="new-password"
                minLength={6}
                required
              />
            </label>
          )}

          {error && (
            <p className="email-auth-message email-auth-error" role="alert">{error}</p>
          )}
          {message && (
            <p className="email-auth-message email-auth-success" role="status">{message}</p>
          )}

          <button className="email-auth-primary" type="submit" disabled={busy || !configured}>
            {busy
              ? t("emailAuthWorking")
              : mode === "signup"
                ? t("emailAuthCreate")
                : t("emailAuthLogin")}
          </button>
        </form>

        {mode === "login" && (
          <>
            <div className="email-auth-divider" aria-hidden="true">{t("googleAuthOr")}</div>
            <button
              className="email-auth-google"
              type="button"
              onClick={signInGoogle}
              disabled={busy || !configured}
            >
              <span aria-hidden="true">G</span>{t("googleAuthContinue")}
            </button>
          </>
        )}

        {canResend && (
          <button className="email-auth-link" type="button" onClick={resend} disabled={busy}>
            {t("emailAuthResend")}
          </button>
        )}

        {mode === "login" && (
          <button className="email-auth-link" type="button" onClick={reset} disabled={busy}>
            {t("emailAuthForgotPassword")}
          </button>
        )}

        <button
          className="email-auth-switch"
          type="button"
          disabled={busy}
          onClick={() => {
            setMode((v) => (v === "login" ? "signup" : "login"));
            setError("");
            setMessage("");
            setCanResend(false);
            setPasswordConfirm("");
          }}
        >
          {mode === "login" ? t("emailAuthNeedAccount") : t("emailAuthHaveAccount")}
        </button>
      </section>
    </div>
  );
}
